'use client';

import { c as _c } from "react/compiler-runtime";
import { Liveline, type LivelineCanonicalFrame, type LivelineFrameSource } from '@morfi/chart';
import { cubicBezier, useReducedMotion } from 'motion/react';
import { useTheme } from 'next-themes';
import type { NamedExoticComponent } from 'react';
import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIntersectionObserver } from '@/hooks/ui/use-intersection-observer';
import { resolveCssColor } from '@/lib/pattern-studio/core/browser/token-resolver';
import { DURATION_POSITION_SMALL_MS, EASE_POSITION_MOTION } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

/** Single point of the total-PnL line, oldest → newest. `time` is unix seconds. */
export interface PnlLivelinePoint {
  readonly time: number;
  readonly value: number;
}
export interface PnlCardLivelineProps {
  /**
   * Total-PnL series, oldest → newest. The card only mounts this wrapper once
   * `adaptTotalPnlLine` has produced a non-null series (length ≥ 2), so the
   * loading / empty branches stay upstream.
   */
  readonly series: ReadonlyArray<PnlLivelinePoint>;
  /** Display-only viewport start; the authoritative lifetime delta stays upstream. */
  readonly startTime?: number;
  /** Drives the line colour from the selected timeframe's delta. */
  readonly tone: 'success' | 'destructive';
  readonly className?: string;
}
type PnlLivelineComponent = NamedExoticComponent<PnlCardLivelineProps>;
const MIN_POINTS = 2;
const SAMPLE_COUNT = 48;
const MORPH_EASING: (progress: number) => number = cubicBezier(...EASE_POSITION_MOTION);

// LiveChart paints into a canvas, which cannot resolve `var(--…)`. Resolve the
// explicit line colour here so it remains the chart's highest-priority colour.
function resolveCssVar(input: string, fallback: string = input): string {
  if (typeof document === 'undefined') return fallback;
  const match = /^var\((--[^,)]+)(?:,\s*([^)]+))?\)$/.exec(input);
  if (!match) return input;
  const varName = match[1] ?? '';
  const resolved = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  if (resolved.length > 0) return resolveCssColor(resolved) ?? resolved;
  const inlineFallback = match[2];
  return inlineFallback !== undefined ? inlineFallback.trim() : fallback;
}

/** A fixed historical viewport keeps the selected history visible across the card. */
function PnlCardLivelineView(t0) {
  const $ = _c(34);
  const {
    series,
    startTime,
    tone,
    className
  } = t0;
  const {
    resolvedTheme
  } = useTheme();
  const theme = resolvedTheme === "light" ? "light" : "dark";
  const reduceMotion = useReducedMotion() === true;
  const containerRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      rootMargin: "200px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    isIntersecting
  } = useIntersectionObserver(containerRef, t1);
  const [mounted, setMounted] = useState(false);
  let t2;
  let t3;
  if ($[1] !== isIntersecting || $[2] !== mounted) {
    t2 = () => {
      if (isIntersecting && !mounted) {
        setMounted(true);
      }
    };
    t3 = [isIntersecting, mounted];
    $[1] = isIntersecting;
    $[2] = mounted;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const t4 = tone === "destructive" ? "var(--data-loss)" : "var(--data-profit)";
  let t5;
  if ($[5] !== t4) {
    t5 = resolveCssVar(t4);
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const color = t5;
  let lastValue;
  let points;
  let shapeWindow;
  let t6;
  if ($[7] !== series || $[8] !== startTime) {
    points = sampleHistory(series, startTime);
    lastValue = points[points.length - 1]?.value ?? 0;
    shapeWindow = Math.max(1, points.length - 1);
    t6 = createShapeFrame(points);
    $[7] = series;
    $[8] = startTime;
    $[9] = lastValue;
    $[10] = points;
    $[11] = shapeWindow;
    $[12] = t6;
  } else {
    lastValue = $[9];
    points = $[10];
    shapeWindow = $[11];
    t6 = $[12];
  }
  const frame = t6;
  const frameRef = useRef(frame);
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => ({
      morphDurationMs: DURATION_POSITION_SMALL_MS,
      morphEasing: MORPH_EASING,
      getFrame: () => frameRef.current
    });
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const [frameSource] = useState(t7);
  let t8;
  let t9;
  if ($[14] !== frame) {
    t8 = () => {
      frameRef.current = frame;
    };
    t9 = [frame];
    $[14] = frame;
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  useLayoutEffect(t8, t9);
  let t10;
  if ($[17] !== reduceMotion) {
    t10 = reduceMotion ? {
      lerpSpeed: 1
    } : {};
    $[17] = reduceMotion;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const smoothingProp = t10;
  let t11;
  if ($[19] !== className) {
    t11 = cn("pointer-events-none relative h-full w-full", className);
    $[19] = className;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== color || $[22] !== frameSource || $[23] !== lastValue || $[24] !== mounted || $[25] !== points || $[26] !== shapeWindow || $[27] !== smoothingProp || $[28] !== theme) {
    t12 = mounted && points.length >= MIN_POINTS ? <Liveline data={points} series={[{
      id: "portfolio",
      data: points,
      value: lastValue,
      color
    }]} value={lastValue} color={color} lineWidth={4} curve="monotone" theme={theme} window={shapeWindow} windowBuffer={0} frameSource={frameSource} extendRange={_temp} grid={false} formatTime={_temp2} scrub={false} pulse={false} dotSize={0} valueLine={false} timeAxis={false} edgeFade={false} nowSeconds={shapeWindow} exaggerate={true} fill={true} badge={false} momentum={false} {...smoothingProp} padding={{
      top: 16,
      right: 0,
      bottom: 16,
      left: 0
    }} className="block h-full w-full" /> : null;
    $[21] = color;
    $[22] = frameSource;
    $[23] = lastValue;
    $[24] = mounted;
    $[25] = points;
    $[26] = shapeWindow;
    $[27] = smoothingProp;
    $[28] = theme;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t11 || $[31] !== t12 || $[32] !== tone) {
    t13 = <div ref={containerRef} className={t11} aria-hidden={true} role="presentation" data-testid="pnl-card-liveline" data-tone={tone}>{t12}</div>;
    $[30] = t11;
    $[31] = t12;
    $[32] = tone;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  return t13;
}
function _temp2() {
  return "";
}
function _temp() {
  return {
    min: -0.05,
    max: 1.05
  };
}
function createShapeFrame(points: ReadonlyArray<PnlLivelinePoint>): LivelineCanonicalFrame {
  const minimum = Math.min(...points.map(point => point.value));
  const maximum = Math.max(...points.map(point => point.value));
  const span = maximum - minimum;
  // The axis-free preview morphs matching screen positions instead of zooming date windows.
  const shape = points.map((point, index) => ({
    time: index,
    value: span === 0 ? 0.5 : (point.value - minimum) / span
  }));
  const head = Math.max(1, points.length - 1);
  return {
    revision: points.map(point => `${point.time}:${point.value}`).join('|'),
    earliestAffectedTime: 0,
    logicalNowSeconds: head,
    nowSeconds: head,
    series: [{
      id: 'portfolio',
      data: shape,
      value: shape.at(-1)?.value ?? 0.5
    }]
  };
}
function sampleHistory(series: ReadonlyArray<PnlLivelinePoint>, startTime: number | undefined): PnlLivelinePoint[] {
  const first = series[0];
  const last = series.at(-1);
  if (first === undefined || last === undefined || last.time <= first.time) return [...series];
  const start = startTime !== undefined && Number.isFinite(startTime) ? Math.max(first.time, Math.min(startTime, last.time)) : first.time;
  let index = 0;
  return Array.from({
    length: SAMPLE_COUNT
  }, (_, sample) => {
    const time = start + (last.time - start) * sample / (SAMPLE_COUNT - 1);
    while (index < series.length - 2 && (series[index + 1]?.time ?? last.time) < time) index += 1;
    const left = series[index] ?? first;
    const right = series[index + 1] ?? last;
    const fraction = right.time === left.time ? 0 : (time - left.time) / (right.time - left.time);
    return {
      time,
      value: left.value + (right.value - left.value) * fraction
    };
  });
}
function sameProps(previous: PnlCardLivelineProps, next: PnlCardLivelineProps): boolean {
  if (previous.tone !== next.tone || previous.startTime !== next.startTime || previous.className !== next.className || previous.series.length !== next.series.length) {
    return false;
  }
  // ALLOW: React Doctor cannot follow the preceding early-return length guard.
  // REASON: The arrays are already proven equal-length before the deep point comparison.
  // react-doctor-disable-next-line react-doctor/js-length-check-first -- length equality is checked above.
  return previous.series.every((point, index) => point.time === next.series[index]?.time && point.value === next.series[index]?.value);
}

// ALLOW: The canvas boundary requires value-based isolation across rebuilt series arrays.
// REASON: React Compiler uses identity dependencies and cannot replace this deep point comparator.
// react-doctor-disable-next-line react-doctor/react-compiler-no-manual-memoization -- intentional canvas isolation.
const Isolated: PnlLivelineComponent = memo(PnlCardLivelineView, sameProps); // ARBITRARY: canvas isolation

export const PnlCardLiveline: PnlLivelineComponent = Isolated;